import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Plus,
  X,
  BookOpen,
  Eye,
  Headphones,
  Clock,
  Calendar,
  TrendingUp,
  Loader2,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";

interface UserStats {
  bookViews: number;
  bookReads: number;
  audioPlays: number;
  totalMinutesRead: number;
  totalPagesRead: number;
  favoriteCategories: string[];
}

interface UserCategory {
  id: number;
  name: string;
}

interface UserStatisticsProps {
  user: {
    id: number;
    name: string;
    registeredAt: string;
  };
  userStats: UserStats;
  userCategoryData: UserCategory[];
  hasAdminPrivileges: boolean;
  canManageUserRoles: boolean;
  isAddingCategories: boolean;
  isLoading?: boolean;
  onOpenCategoryPicker: () => void;
  onRemoveCategory: (categoryId: number, categoryName: string) => void;
}

// Loading skeleton component
const StatSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn("bg-muted/30 rounded-md p-3 text-center border", className)}
  >
    <div className="flex items-center justify-center gap-1 mb-1">
      <div className="h-4 w-4 bg-muted-foreground/20 rounded animate-pulse" />
    </div>
    <div className="h-6 w-12 bg-muted-foreground/20 rounded mx-auto mb-1 animate-pulse" />
    <div className="h-3 w-16 bg-muted-foreground/20 rounded mx-auto animate-pulse" />
  </div>
);

const UserStatistics: React.FC<UserStatisticsProps> = ({
  user,
  userStats,
  userCategoryData,
  hasAdminPrivileges,
  canManageUserRoles,
  isAddingCategories,
  isLoading = false,
  onOpenCategoryPicker,
  onRemoveCategory,
}) => {
  // Calculate account age in days
  const accountAgeDays = differenceInDays(
    new Date(),
    new Date(user.registeredAt)
  );

  // Determine user status based on activity and account age
  const getUserStatus = () => {
    if (isLoading) {
      return { label: "Loading...", variant: "secondary" as const };
    }

    const totalActivity =
      userStats.bookViews + userStats.bookReads + userStats.audioPlays;

    if (accountAgeDays < 7)
      return { label: "New User", variant: "secondary" as const };
    if (totalActivity === 0)
      return { label: "Inactive", variant: "outline" as const };
    if (totalActivity < 5)
      return { label: "Light User", variant: "outline" as const };
    if (totalActivity < 20)
      return { label: "Regular User", variant: "default" as const };
    return { label: "Power User", variant: "default" as const };
  };

  const userStatus = getUserStatus();

  // Calculate engagement metrics
  const totalEngagement = isLoading
    ? 0
    : userStats.bookViews + userStats.bookReads + userStats.audioPlays;
  const avgMinutesPerRead = isLoading
    ? 0
    : userStats.bookReads > 0
    ? Math.round(userStats.totalMinutesRead / userStats.bookReads)
    : 0;
  const avgPagesPerRead = isLoading
    ? 0
    : userStats.bookReads > 0
    ? Math.round(userStats.totalPagesRead / userStats.bookReads)
    : 0;

  // Format total minutes to "Xh Ym"
  const formatMinutesToReadableTime = (totalMinutes: number): string => {
    if (!totalMinutes || totalMinutes <= 0) return "0m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          User Statistics
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Account Overview */}
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Account Overview
            </p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{accountAgeDays} days</p>
                <p className="text-sm text-muted-foreground">
                  Member since{" "}
                  {format(new Date(user.registeredAt), "MMM dd, yyyy")}
                </p>
              </div>
              <Badge variant={userStatus.variant}>{userStatus.label}</Badge>
            </div>
          </div>

          {/* Reading Activity */}
          <div>
            <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Reading Activity
            </p>
            <div className="grid grid-cols-2 gap-3">
              {isLoading ? (
                <>
                  <StatSkeleton className="border-blue-200 dark:border-blue-800" />
                  <StatSkeleton className="border-green-200 dark:border-green-800" />
                  <StatSkeleton className="border-purple-200 dark:border-purple-800" />
                  <StatSkeleton className="border-orange-200 dark:border-orange-800" />
                </>
              ) : (
                <>
                  <div className="bg-blue-50 dark:bg-blue-950/20 rounded-md p-3 text-center border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                      {userStats.bookViews}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Book Views
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-md p-3 text-center border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <BookOpen className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-xl font-bold text-green-700 dark:text-green-300">
                      {userStats.bookReads}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      Books Read
                    </p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-950/20 rounded-md p-3 text-center border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                      {formatMinutesToReadableTime(userStats.totalMinutesRead)}
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400">
                      Total Reading Time
                    </p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-950/20 rounded-md p-3 text-center border border-orange-200 dark:border-orange-800">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Headphones className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <p className="text-xl font-bold text-orange-700 dark:text-orange-300">
                      {userStats.audioPlays}
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400">
                      Audio Plays
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Engagement Insights */}
          {(isLoading || totalEngagement > 0) && (
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                Engagement Insights
              </p>
              <div className="grid grid-cols-1 gap-3">
                {isLoading ? (
                  <>
                    <div className="bg-muted/30 rounded-md p-3">
                      <div className="flex justify-between items-center">
                        <div className="h-4 w-24 bg-muted-foreground/20 rounded animate-pulse" />
                        <div className="h-4 w-20 bg-muted-foreground/20 rounded animate-pulse" />
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-md p-3">
                      <div className="flex justify-between items-center">
                        <div className="h-4 w-28 bg-muted-foreground/20 rounded animate-pulse" />
                        <div className="h-4 w-24 bg-muted-foreground/20 rounded animate-pulse" />
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-md p-3">
                      <div className="flex justify-between items-center">
                        <div className="h-4 w-20 bg-muted-foreground/20 rounded animate-pulse" />
                        <div className="h-4 w-28 bg-muted-foreground/20 rounded animate-pulse" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-muted/30 rounded-md p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Total Engagement
                        </span>
                        <span className="font-semibold">
                          {totalEngagement} actions
                        </span>
                      </div>
                    </div>
                    {userStats.bookReads > 0 && (
                      <>
                        {avgMinutesPerRead > 0 && (
                          <div className="bg-muted/30 rounded-md p-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted-foreground">
                                Avg. Reading Time
                              </span>
                              <span className="font-semibold">
                                {avgMinutesPerRead} min/book
                              </span>
                            </div>
                          </div>
                        )}
                        {avgPagesPerRead > 0 && (
                          <div className="bg-muted/30 rounded-md p-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted-foreground">
                                Avg. Pages Read
                              </span>
                              <span className="font-semibold">
                                {avgPagesPerRead} pages/book
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {accountAgeDays > 0 && totalEngagement > 0 && (
                      <div className="bg-muted/30 rounded-md p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            Activity Rate
                          </span>
                          <span className="font-semibold">
                            {(
                              totalEngagement / Math.max(accountAgeDays, 1)
                            ).toFixed(1)}{" "}
                            actions/day
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* User Categories */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                Selected Categories
              </p>
              {hasAdminPrivileges && canManageUserRoles && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={onOpenCategoryPicker}
                  disabled={isAddingCategories || isLoading}
                  className="text-xs h-6 w-6 p-0"
                  title="Add Categories"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {isLoading ? (
                <div className="flex gap-2">
                  <div className="h-6 w-16 bg-muted-foreground/20 rounded animate-pulse" />
                  <div className="h-6 w-20 bg-muted-foreground/20 rounded animate-pulse" />
                  <div className="h-6 w-14 bg-muted-foreground/20 rounded animate-pulse" />
                </div>
              ) : userCategoryData.length > 0 ? (
                userCategoryData.map((category) => (
                  <div key={category.id} className="group relative">
                    <Badge variant="secondary" className="pr-6">
                      {category.name}
                    </Badge>
                    {hasAdminPrivileges && canManageUserRoles && (
                      <button
                        onClick={() =>
                          onRemoveCategory(category.id, category.name)
                        }
                        disabled={isAddingCategories}
                        className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center hover:bg-destructive/80 disabled:opacity-50"
                        title={`Remove ${category.name}`}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  No categories selected during onboarding
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserStatistics;
